import React, { useMemo, useState } from 'react';
import Funnel from '@components/Widgets/Funnel';
import Pie from '@components/Widgets/VisualizationWidgets/AvPie';
import Widget from '@components/Widgets/Widget';
import { DateCondition, FeatureFlags, Filter, PeriodBehavior, RelativeUnit } from '@fabric-ui/components';
import { TimeBucket } from '@fabric-ui/components/Table';
import { flex } from '@fabric-ui/theme';
import { Box, Collapse, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { useWidgetData } from '@utils/dashboardDataUtils';
import { cleanEmptyFilters, filterToExpression } from '@utils/filterUtils';
import { useSnackbar } from 'notistack';
import { PieWidgetCloneAdapter } from '../../../components/Widgets/VisualizationWidgets/PieWidgetCloneAdapter';
import { useAvContext } from '../../../context/AvContextProvider';
import useQueryObject from '../../../hooks/useQueryObjectSql';
import useQuerySql from '../../../hooks/useQuerySql';
import DraggableWidget from '../../CustomDashboards/components/DraggableWidget';
import renderWidget from '../../CustomDashboards/components/Widgets/RenderWidget';
import { DwQueryRequest, PieSubType, SortDir } from '../../CustomDashboards/types';
import { getEntityOverTimeWidget } from '../../Policies/AssetInventoryDashboard/Widgets';
import { filtersFieldToIgnore } from '../ticket.types';
import { useBuildWhereClause } from '../useBuildWhereClause';
import AlertScoreWidgetEmptyState from './AlertScoreWidgetEmptyState';
import { EVariant, IEntityViewChartsProps } from './types';
import { entityViewWidgetStyle, EntityViewWidgetWrapper, getKeyValueData } from './utils';

const getAlertsOverTimeQuery = ({
  timeRange,
  dateGap,
  filter,
  secondDimName,
  metricName = 'total_incident_alerts_granular',
}: {
  timeRange: DateCondition;
  dateGap;
  filter?: Filter;
  secondDimName?: string;
  metricName?: string;
}): DwQueryRequest => ({
  groupBy: [dateGap, ...(secondDimName ? [secondDimName] : [])],
  distinct: false,
  filter,
  select: {
    metrics: [
      {
        name: metricName,
        alias: metricName,
      },
    ],
    dims: [
      {
        name: dateGap,
      },
      ...(secondDimName ? [{ name: secondDimName }] : []),
    ],
  },
  sorting: [
    {
      name: dateGap,
      dir: SortDir.ASC,
    },
  ],
  top: {
    size: 100,
    groupOthers: false,
    offset: 0,
  },
  projectionId: {
    name: 'uber_alerts',
    builtIn: true,
  },
  timeRange,
});
const AlertsToIncidentsConfig = {
  title: 'Alerts to Incidents',
  sql: where =>
    `SELECT
       active_alerts_granular AS alerts_count,
       total_incidents_granular AS incidents_count,
       active_incidents_granular AS active_incident
     FROM uber_alerts
     ${where}`,
  transformData: data => {
    const [{ active_incident: count3, alerts_count: count1, incidents_count: count2 }] = data;
    return count1
      ? [
          { name: 'Alerts', value: count1, color: 'var(--dataviz-color-sequential-blue-scale03)' },
          {
            name: 'Incidents',
            value: count2,
            color: 'var(--dataviz-color-sequential-blue-scale04)',
            ratio: Math.round(count1 / count2),
          },
          { name: 'Active Incidents', value: count3, color: 'var(--dataviz-color-sequential-blue-scale06)' },
        ].map(item => ({ ...item, displayValues: [item.value, item.ratio] }))
      : [];
  },
};

const relativeUnitToTimeBucketMap = {
  [RelativeUnit.MONTHS]: TimeBucket.BUCKET_MONTH,
  [RelativeUnit.WEEKS]: TimeBucket.BUCKET_WEEK,
  [RelativeUnit.DAYS]: TimeBucket.BUCKET_DAY,
};

const AlertOverTimeAndProductWidget = ({
  filter,
  relativeUnit,
  setRelativeUnit,
  timeDimension,
}: {
  filter?: Filter;
  relativeUnit: RelativeUnit;
  setRelativeUnit: (v: RelativeUnit) => void;
  timeDimension: { timeRange: DateCondition; dateGap: typeof TimeBucket };
}) => (
  <Box sx={{ ...entityViewWidgetStyle, ...flex.col }}>
    <DraggableWidget
      widget={getEntityOverTimeWidget({
        req: getAlertsOverTimeQuery({
          ...timeDimension,
          filter,
          secondDimName: 'alert.product',
        }),
      })}
      customToolbar={
        <Box sx={flex.justifyBetweenStart}>
          <Typography sx={{ whiteSpace: 'nowrap', mb: 2 }} variant="h6">
            Number of Alerts by Product Over Time
          </Typography>
          <ToggleButtonGroup size="xSmall" color="white" onChange={(e: any) => setRelativeUnit(e.target.value)} value={relativeUnit}>
            <ToggleButton value={RelativeUnit.DAYS}>Day</ToggleButton>
            <ToggleButton value={RelativeUnit.WEEKS}>Week</ToggleButton>
            <ToggleButton value={RelativeUnit.MONTHS}>Month</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      }>
      {renderWidget({
        widget: getEntityOverTimeWidget({
          req: getAlertsOverTimeQuery({
            ...timeDimension,
            filter,
            secondDimName: 'alert.product',
          }),
        }),
      })}
    </DraggableWidget>
  </Box>
);

const AlertScoreBucketWidget = ({
  where,
  enabled,
  isScoreEmpty,
  setIsScoreEmpty,
}: {
  where: string;
  enabled: boolean;
  isScoreEmpty: boolean;
  setIsScoreEmpty: (v: boolean) => void;
}) => {
  const widgetData = useWidgetData();
  const { enqueueSnackbar } = useSnackbar();
  const { featureFlags } = useAvContext();
  const usePieV2 = Boolean(featureFlags[FeatureFlags.PieChartV2]);

  if (isScoreEmpty) {
    return <AlertScoreWidgetEmptyState title={widgetData.AlertScoresBucket.title} />;
  }

  return (
    <Widget
      {...widgetData.AlertScoresBucket}
      sql={widgetData.AlertScoresBucket.sql(where)}
      onError={e => {
        if (e.extensions.message === "Duplicate field key '0 to 0'") {
          setIsScoreEmpty(true);
        } else {
          enqueueSnackbar(e.message, { variant: 'error' });
          console.error(e);
        }
      }}
      metricKey="bucket"
      queryOptions={{ placeholderData: keepPreviousData, enabled }}
      sx={{
        height: 280,
        ...entityViewWidgetStyle,
      }}
      titleVariant="h6">
      {usePieV2 ? (
        <PieWidgetCloneAdapter variant="pie" legendType="table-right" legendProps={{ showValue: false, showShare: true }} />
      ) : (
        <Pie legendProps={{ variant: 'static', maxMetricWidth: 110, showShare: true }} type={PieSubType.Pie} tableSx={{ py: 0 }} />
      )}
    </Widget>
  );
};

const AlertScoreBucketWidgetMemoized = React.memo(AlertScoreBucketWidget);

function AlertsToIncidentsWidget({ where, enabled }: { where: string; enabled: boolean }) {
  return (
    <Box sx={entityViewWidgetStyle}>
      <Widget
        title={AlertsToIncidentsConfig.title}
        titleVariant="h6"
        titleMargin={1}
        sql={AlertsToIncidentsConfig.sql(where)}
        transformData={data => AlertsToIncidentsConfig.transformData(data)}
        sx={{ height: '100%', pb: 1 }}
        queryOptions={{ placeholderData: keepPreviousData, enabled }}>
        <Funnel height="100%" barSize={16} labelsSize={12} />
      </Widget>
    </Box>
  );
}

const AlertsToIncidentsWidgetMemoized = React.memo(AlertsToIncidentsWidget);

const AlertsViewCharts: React.FC<IEntityViewChartsProps> = ({
  filters: f,
  isExpanded = true,
  setIsExpanded,
  entityTypeId,
  extraFilters,
  searchFields,
}) => {
  const widgetData = useWidgetData();
  const {
    accountEntities: { fieldTypeMap },
  } = useAvContext();
  const [isScoreEmpty, setIsScoreEmpty] = useState(false);
  const [relativeUnit, setRelativeUnit] = useState(RelativeUnit.MONTHS);

  const timeDimension = useMemo(
    () => ({
      timeRange: {
        relative: {
          periodBehaviour: PeriodBehavior.last,
          value: 6,
          unit: relativeUnit,
        },
      },
      dateGap: relativeUnitToTimeBucketMap[relativeUnit],
    }),
    [relativeUnit]
  );

  const filters = Object.keys(f).reduce(
    (acc, field) =>
      (!filtersFieldToIgnore.includes(field) || field === 'search') && !!f[field].length
        ? {
            ...acc,
            [field]: f[field],
          }
        : acc,
    { search: [] }
  );
  const regularFilters = filterToExpression(fieldTypeMap, filters);
  const cleanFilters = cleanEmptyFilters(regularFilters);
  const filtersWhere = useBuildWhereClause({
    entityTypeId,
    filters,
    allowMetrics: true,
    extra: extraFilters,
    searchFields,
  });

  const { data: alertsToIncidentsData, isLoading: alertsToIncidentsLoading } = useQuerySql({
    key: 'AlertsToIncidents',
    sql: AlertsToIncidentsConfig.sql(filtersWhere),
    options: { placeholderData: keepPreviousData, enabled: !isExpanded },
    onSuccess: data => AlertsToIncidentsConfig.transformData(data),
  });

  const { data: alertScoresBucketData, isLoading: alertScoresBucketLoading } = useQuerySql({
    key: `${widgetData.AlertScoresBucket}-summary`,
    sql: widgetData.AlertScoresBucket.sql(filtersWhere),
    options: { placeholderData: keepPreviousData, enabled: !isExpanded && !isScoreEmpty },
    onSuccess: data =>
      Object.fromEntries(
        Object.entries(data[0].res)
          .sort((a, b) => Number(b[1]) - Number(a[1]))
          .slice(0, 2)
      ),
  });

  const alertsOverTimeReq = getAlertsOverTimeQuery({
    ...timeDimension,
    filter: cleanFilters,
    secondDimName: 'alert.product',
  });
  const { data: alertsOverTimeData, isLoading: alertsOverTimeLoading } = useQueryObject({
    queryObject: alertsOverTimeReq,
    options: { placeholderData: keepPreviousData, enabled: !isExpanded },
    onSuccess: data =>
      getKeyValueData({
        data,
        dataKey: alertsOverTimeReq.select.metrics[0].name,
        titleKey: alertsOverTimeReq.select.dims[1].name,
        nullValueHeader: 'No Product',
      }),
  });

  const summaryItems = useMemo(
    () => [
      {
        title: 'Alerts over time and product',
        data: alertsOverTimeData,
        isLoading: alertsOverTimeLoading,
        showIcon: false,
      },
      ...(!isScoreEmpty
        ? [
            {
              title: widgetData.AlertScoresBucket.title!,
              data: alertScoresBucketData,
              isLoading: alertScoresBucketLoading,
              showIcon: false,
              variant: EVariant.percentage,
            },
          ]
        : []),
      {
        title: AlertsToIncidentsConfig.title,
        data: alertsToIncidentsData,
        isLoading: alertsToIncidentsLoading,
        onSuccess: data =>
          data?.reduce((acc, curr) => (['Alerts', 'Active Incidents'].includes(curr.name) ? { ...acc, [curr.name]: curr.value } : acc), {}),
        showIcon: false,
        variant: EVariant.transition,
      },
    ],
    [
      alertsToIncidentsData,
      alertsToIncidentsLoading,
      alertScoresBucketData,
      alertScoresBucketLoading,
      alertsOverTimeData,
      alertsOverTimeLoading,
    ]
  );

  return (
    <EntityViewWidgetWrapper isExpanded={isExpanded} setIsExpanded={setIsExpanded} summaryItemsProps={{ items: summaryItems }}>
      <Collapse in={isExpanded} timeout="auto">
        <Box sx={{ ...flex.row, gap: 1.5, height: '208px' }}>
          <AlertOverTimeAndProductWidget
            filter={cleanFilters}
            relativeUnit={relativeUnit}
            setRelativeUnit={setRelativeUnit}
            timeDimension={timeDimension}
          />
          <AlertScoreBucketWidgetMemoized
            where={filtersWhere}
            enabled={isExpanded}
            isScoreEmpty={isScoreEmpty}
            setIsScoreEmpty={setIsScoreEmpty}
          />
          <AlertsToIncidentsWidgetMemoized where={filtersWhere} enabled={isExpanded} />
        </Box>
      </Collapse>
    </EntityViewWidgetWrapper>
  );
};

export default AlertsViewCharts;
